import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Autocomplete,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { PageTitle } from '../../../../components/common';
import { usePrecioClienteStore } from '../store/usePrecioClienteStore';
import { clienteService } from '../../partes-produccion/services/clienteService';
import type { ClienteSearch } from '../../partes-produccion/services/clienteService';
import { EspecieService } from '../../../catalogo/especies/services/especieService';
import type { PresentacionListItem, CalibreListItem } from '../../../catalogo/especies/types/especie';
import { TIPOS_PROCESO } from '../types';
import type { PrecioCliente } from '../types';

const PreciosClientePage: React.FC = () => {
  const [selectedCliente, setSelectedCliente] = useState<ClienteSearch | null>(null);
  const [clienteOptions, setClienteOptions] = useState<ClienteSearch[]>([]);
  const [clienteInputValue, setClienteInputValue] = useState('');
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [selectedTipoProceso, setSelectedTipoProceso] = useState<string>('COMPRA');
  const [selectedPresentacion, setSelectedPresentacion] = useState<number | 'TODAS'>('TODAS');
  const [editablePrecios, setEditablePrecios] = useState<Map<string, { precioMP: number; precioServ: number }>>(new Map());
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [presentaciones, setPresentaciones] = useState<PresentacionListItem[]>([]);
  const [calibres, setCalibres] = useState<CalibreListItem[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const {
    precios,
    loading,
    error,
    fetchByClienteAndTipo,
    saveBulk,
    clearError,
  } = usePrecioClienteStore();

  // Search clientes when input changes
  useEffect(() => {
    if (clienteInputValue.length < 2) {
      setClienteOptions([]);
      return;
    }

    const searchClientes = async () => {
      setLoadingClientes(true);
      try {
        const results = await clienteService.search(clienteInputValue);
        setClienteOptions(results);
      } catch (err) {
        console.error('Error searching clientes:', err);
      } finally {
        setLoadingClientes(false);
      }
    };

    const debounce = setTimeout(searchClientes, 300);
    return () => clearTimeout(debounce);
  }, [clienteInputValue]);

  useEffect(() => {
    const loadCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [pres, cal] = await Promise.all([
          EspecieService.getPresentaciones(),
          EspecieService.getCalibres(),
        ]);
        setPresentaciones(pres);
        setCalibres(cal);
      } catch (err) {
        console.error('Error loading catalog data:', err);
      } finally {
        setLoadingCatalog(false);
      }
    };
    loadCatalog();
  }, []);

  useEffect(() => {
    if (selectedCliente && selectedTipoProceso) {
      fetchByClienteAndTipo(
        selectedCliente.id,
        selectedTipoProceso
      );
    }
  }, [selectedCliente, selectedTipoProceso, fetchByClienteAndTipo]);

  useEffect(() => {
    const map = new Map<string, { precioMP: number; precioServ: number }>();
    for (const p of precios) {
      const key = `${p.idPresentacion}-${p.idCalibre}`;
      map.set(key, { precioMP: p.precioMateriaPrima, precioServ: p.precioServicio });
    }
    setEditablePrecios(map);
  }, [precios]);

  const handlePrecioChange = (
    idPresentacion: number,
    idCalibre: number,
    field: 'precioMP' | 'precioServ',
    value: string
  ) => {
    const key = `${idPresentacion}-${idCalibre}`;
    const current = editablePrecios.get(key) || { precioMP: 0, precioServ: 0 };
    const updated = { ...current, [field]: parseFloat(value) || 0 };
    setEditablePrecios(new Map(editablePrecios).set(key, updated));
  };

  const handleSave = async () => {
    if (!selectedCliente) return;

    const preciosList: PrecioCliente[] = [];
    for (const [key, values] of editablePrecios) {
      const [idPresentacion, idCalibre] = key.split('-').map(Number);
      if (values.precioMP > 0 || values.precioServ > 0) {
        preciosList.push({
          id: 0,
          idCliente: selectedCliente.id,
          idPresentacion,
          idCalibre,
          tipoProceso: selectedTipoProceso,
          precioMateriaPrima: values.precioMP,
          precioServicio: values.precioServ,
          fechaActualizacion: new Date().toISOString(),
          activo: true,
        });
      }
    }

    const success = await saveBulk({
      idCliente: selectedCliente.id,
      tipoProceso: selectedTipoProceso,
      precios: preciosList,
    });

    if (success) {
      setSuccessMsg('Precios guardados exitosamente');
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchByClienteAndTipo(
        selectedCliente.id,
        selectedTipoProceso
      );
    }
  };

  const presentacionesFiltradas = selectedPresentacion === 'TODAS'
    ? presentaciones
    : presentaciones.filter((p) => p.idPresentacion === selectedPresentacion);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <PageTitle
        title="Precios Cliente"
        subtitle="Configuración de precios de materia prima y servicio por cliente"
      />

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2, borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2, borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}>
          {successMsg}
        </Alert>
      )}

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          bgcolor: '#fff',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontWeight: 700,
            fontSize: '0.75rem',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 2,
          }}
        >
          Filtros
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            sx={{ minWidth: 300, flex: 1 }}
            options={clienteOptions}
            getOptionLabel={(option) => option.razonSocial}
            value={selectedCliente}
            onChange={(_e, newValue) => setSelectedCliente(newValue)}
            inputValue={clienteInputValue}
            onInputChange={(_e, newInputValue) => setClienteInputValue(newInputValue)}
            loading={loadingClientes}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente"
                placeholder="Buscar cliente..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClientes ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options, { inputValue }) => {
              const search = inputValue.toLowerCase();
              return options.filter(
                (o) =>
                  o.razonSocial.toLowerCase().includes(search) ||
                  (o.nroIdentificacion || '').toLowerCase().includes(search)
              );
            }}
            noOptionsText={clienteInputValue.length < 2 ? 'Escriba al menos 2 caracteres' : 'No se encontraron clientes'}
          />

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Tipo Proceso</InputLabel>
            <Select
              value={selectedTipoProceso}
              label="Tipo Proceso"
              onChange={(e) => setSelectedTipoProceso(e.target.value)}
            >
              {TIPOS_PROCESO.options.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Presentación</InputLabel>
            <Select
              value={selectedPresentacion}
              label="Presentación"
              onChange={(e) => setSelectedPresentacion(e.target.value as number | 'TODAS')}
            >
              <MenuItem value="TODAS">Todas</MenuItem>
              {presentaciones.map((pres) => (
                <MenuItem key={pres.idPresentacion} value={pres.idPresentacion}>
                  {pres.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!selectedCliente || loading}
            sx={{
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              background: 'linear-gradient(135deg, #0f2847 0%, #1a4a6e 100%)',
              boxShadow: '0 2px 8px rgba(15,40,71,0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a3a5c 0%, #2a5a7e 100%)',
                boxShadow: '0 4px 12px rgba(15,40,71,0.35)',
              },
            }}
          >
            Guardar Precios
          </Button>
        </Box>
      </Paper>

      {(loading || loadingCatalog) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#3b82f6' }} />
        </Box>
      )}

      {/* Tables por presentacion */}
      {selectedCliente && !loading && !loadingCatalog && presentacionesFiltradas.length > 0 && (
        <>
          {presentacionesFiltradas.map((pres) => {
            const calibresPres = calibres.filter(
              (c) => c.idPresentacion === pres.idPresentacion
            );
            if (calibresPres.length === 0) return null;

            return (
              <Paper
                key={pres.idPresentacion}
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: '#f8fafc',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#3b82f6',
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: '#0f2847',
                    }}
                  >
                    {pres.descripcion}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          Calibre
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          Precio M.P. (S/.)
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          Precio Servicio (S/.)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calibresPres.map((cal) => {
                        const key = `${pres.idPresentacion}-${cal.idCalibre}`;
                        const valores = editablePrecios.get(key) || { precioMP: 0, precioServ: 0 };

                        return (
                          <TableRow
                            key={cal.idCalibre}
                            sx={{
                              '&:hover': { bgcolor: '#f0f7ff' },
                              transition: 'background-color 0.15s ease',
                            }}
                          >
                            <TableCell
                              sx={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: '#1e293b',
                                borderBottom: '1px solid #f1f5f9',
                              }}
                            >
                              {cal.descripcion || cal.idCalibre}
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                              <TextField
                                size="small"
                                type="number"
                                value={valores.precioMP || ''}
                                onChange={(e) =>
                                  handlePrecioChange(pres.idPresentacion, cal.idCalibre, 'precioMP', e.target.value)
                                }
                                sx={{
                                  width: 120,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '0.85rem',
                                  },
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ borderBottom: '1px solid #f1f5f9' }}>
                              <TextField
                                size="small"
                                type="number"
                                value={valores.precioServ || ''}
                                onChange={(e) =>
                                  handlePrecioChange(pres.idPresentacion, cal.idCalibre, 'precioServ', e.target.value)
                                }
                                sx={{
                                  width: 120,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: '0.85rem',
                                  },
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </>
      )}

      {selectedCliente && !loading && !loadingCatalog && presentacionesFiltradas.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}>
          No hay presentaciones configuradas. Configure presentaciones en el catálogo de especies.
        </Alert>
      )}

      {!selectedCliente && (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            textAlign: 'center',
            bgcolor: '#fff',
          }}
        >
          <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.95rem', color: '#94a3b8' }}>
            Seleccione un cliente para ver y editar sus precios.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PreciosClientePage;
